<template>
  <div class="admin-dashboard">
    <!-- Navigation -->
    <main-navigation></main-navigation>
    
    <!-- Dashboard Header -->
    <div class="dashboard-header bg-primary text-white py-4">
      <div class="container">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h1 class="h3 mb-1">Bursary Administration Dashboard</h1>
            <p class="mb-0 opacity-75">Monitor applications, track approvals, and manage bursary distribution</p>
          </div>
          <div class="text-end">
            <div class="fs-5">{{ currentDate }}</div>
            <small class="opacity-75">Last updated: Just now</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Dashboard Content -->
    <div class="container py-4">
      <!-- Quick Stats Cards -->
      <div class="row g-4 mb-4">
        <div class="col-xl-3 col-md-6">
          <div class="stat-card card border-0 shadow-sm h-100">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="text-uppercase text-muted mb-2">Total </h6>
                  <h2 class="mb-0 fw-bold text-primary">{{ total_applicants }}</h2>
                </div>
                <div class="stat-icon bg-primary bg-opacity-10 rounded-circle p-3">
                  <i class="bi bi-people-fill text-primary fs-3"></i>
                </div>
              </div>
              <div class="mt-3">
                <small class="text-muted">
                  <i class="bi bi-mortarboard me-1"></i> {{ secondaryStudents.length }} Sec
                  <span class="mx-2">•</span>
                  <i class="bi bi-building me-1"></i> {{ collegeStudents.length }} Uni
                </small>
              </div>
            </div>
          </div>
        </div>
        
        <div class="col-xl-3 col-md-6">
          <div class="stat-card card border-0 shadow-sm h-100">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="text-uppercase text-muted mb-2">Applications Approved</h6>
                  <h2 class="mb-0 fw-bold text-success">{{ approvedSecondary.length + approvedCollege.length }}</h2>

                  <!-- {{ approvedSecondary.length }} new ones -->
                </div>
                <div class="stat-icon bg-success bg-opacity-10 rounded-circle p-3">
                  <i class="bi bi-check-circle-fill text-success fs-3"></i>
                </div>
              </div>
              <div class="mt-3">
                <small class="text-muted">
                  {{approvedSecondary.length}} Sec
                  <span class="mx-2">•</span>
                  {{approvedCollege.length }} Uni
                </small>
              </div>
            </div>
          </div>
        </div>
        
        <div class="col-xl-3 col-md-6">
          <div class="stat-card card border-0 shadow-sm h-100">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="text-uppercase text-muted mb-2">Applications Rejected</h6>
                  <h2 class="mb-0 fw-bold text-danger">{{ totalRejected }}</h2>
                </div>
                <div class="stat-icon bg-danger bg-opacity-10 rounded-circle p-3">
                  <i class="bi bi-x-circle-fill text-danger fs-3"></i>
                </div>
              </div>
              <div class="mt-3">
                <small class="text-muted">
                  {{ secondary_processedRejected.length }} Sec
                  <span class="mx-2">•</span>
                  {{ college_processedRejected.length }} Uni
                </small>
              </div>
            </div>
          </div>
        </div>
        
        <div class="col-xl-3 col-md-6">
          <div class="stat-card card border-0 shadow-sm h-100">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="text-uppercase text-muted mb-2">Pending Review</h6>
                  <h2 class="mb-0 fw-bold text-warning">{{ total_applicants -  approvedSecondary.length - approvedCollege.length  }}</h2>
                </div>
                <div class="stat-icon bg-warning bg-opacity-10 rounded-circle p-3">
                  <i class="bi bi-clock-fill text-warning fs-3"></i>
                </div>
              </div>
              <div class="mt-3">
                <small class="text-muted">
                  <!-- {{ pendingSecondary }} Sec -->
                  <span class="mx-2">•</span>
                  <!-- {{ pendingCollege }} Uni -->
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Dashboard Grid -->
      <div class="row g-4">
        <!-- Left Column: Detailed Statistics -->
        <div class="col-lg-8">
          <!-- Application Breakdown by Ward -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-white border-0 py-3">
              <h5 class="mb-0 fw-bold">
                <i class="bi bi-geo-alt-fill me-2 text-primary"></i>
                Applications by Ward
              </h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-4" v-for="ward in wardStats" :key="ward.name">
                  <div class="ward-card p-3 border rounded">
                    <h6 class="fw-bold text-primary mb-2">{{ ward.name }}</h6>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <small class="text-muted">Total Applicants</small>
                        <div class="h5 mb-0 fw-bold">{{ ward.total }}</div>
                      </div>
                      <div class="text-end">
                        <small class="text-muted d-block">Sec</small>
                        <div class="fw-bold">{{ ward.secondary }}</div>
                      </div>
                      <div class="text-end">
                        <small class="text-muted d-block">Uni</small>
                        <div class="fw-bold">{{ ward.college }}</div>
                      </div>
                    </div>
                    <div class="progress" style="height: 6px;">
                      <div class="progress-bar bg-primary" 
                           :style="{ width: ward.percentage + '%' }"></div>
                    </div>
                    <small class="text-muted d-block mt-2">
                      {{ ward.percentage }}% of total applications
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Needy Cases Analysis -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-white border-0 py-3">
              <h5 class="mb-0 fw-bold">
                <i class="bi bi-heart-fill me-2 text-danger"></i>
                Needy Cases Analysis
              </h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-4">
                  <div class="needy-card p-3 border rounded bg-danger bg-opacity-5">
                    <h6 class="fw-bold text-danger mb-2">Orphans</h6>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <div class="h3 mb-0 fw-bold">{{ orphanTotal }}</div>
                      <div class="text-end">
                        <small class="text-muted d-block">Uni</small>
                        <div class="fw-bold">{{ collegeOrphan.length }}</div>
                      </div>
                      <div class="text-end">
                        <small class="text-muted d-block">Sec</small>
                        <div class="fw-bold">{{ secondaryOrphan.length }}</div>
                      </div>
                    </div>
                    <small class="text-muted">
                      {{ orphanPercentage }}% of total applicants
                    </small>
                  </div>
                </div>
                
                <div class="col-md-4">
                  <div class="needy-card p-3 border rounded bg-warning bg-opacity-5">
                    <h6 class="fw-bold text-warning mb-2">Single Parent</h6>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <div class="h3 mb-0 fw-bold">{{ singleParentTotal }}</div>
                      <div class="text-end">
                        <small class="text-muted d-block">Uni</small>
                        <div class="fw-bold">{{ collegeOneparent.length }}</div>
                      </div>
                      <div class="text-end">
                        <small class="text-muted d-block">Sec</small>
                        <div class="fw-bold">{{ SecondaryOneparent.length }}</div>
                      </div>
                    </div>
                    <small class="text-muted">
                      {{ singleParentPercentage }}% of total applicants
                    </small>
                  </div>
                </div>
                
                <div class="col-md-4">
                  <div class="needy-card p-3 border rounded bg-success bg-opacity-5">
                    <h6 class="fw-bold text-success mb-2">Both Parents</h6>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <div class="h3 mb-0 fw-bold">{{ bothParentsTotal }}</div>
                      <div class="text-end">
                        <small class="text-muted d-block">Uni</small>
                        <div class="fw-bold">{{ collegeBothparents.length }}</div>
                      </div>
                      <div class="text-end">
                        <small class="text-muted d-block">Secondary</small>
                        <div class="fw-bold">{{ SecondaryBothparent.length }}</div>
                      </div>
                    </div>
                    <small class="text-muted">
                      {{ bothParentsPercentage }}% of total applicants
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column: Quick Actions & Processing -->
        <div class="col-lg-4">
          <!-- Quick Actions Panel -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-white border-0 py-3">
              <h5 class="mb-0 fw-bold">
                <i class="bi bi-lightning-fill me-2 text-warning"></i>
                Quick Actions
              </h5>
            </div>
            <div class="card-body">
              <div class="list-group list-group-flush">
                <router-link to="/secondary/students" class="list-group-item list-group-item-action border-0 py-3">
                  <div class="d-flex align-items-center">
                    <div class="action-icon bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                      <i class="bi bi-mortarboard text-primary"></i>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="mb-1">Secondary School Students</h6>
                      <small class="text-muted">{{ secondaryStudents.length }} applications</small>
                    </div>
                    <i class="bi bi-chevron-right text-muted"></i>
                  </div>
                </router-link>
                
                <router-link to="/college/students" class="list-group-item list-group-item-action border-0 py-3">
                  <div class="d-flex align-items-center">
                    <div class="action-icon bg-success bg-opacity-10 rounded-circle p-2 me-3">
                      <i class="bi bi-building text-success"></i>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="mb-1">College & University Students</h6>
                      <!-- {{ college_processedApproved }} approved college -->
                      <small class="text-muted">{{ collegeStudents.length }} applicationsz</small>
                    </div>
                    <i class="bi bi-chevron-right text-muted"></i>
                  </div>
                </router-link>
                
                <router-link to="/filter/secondary" class="list-group-item list-group-item-action border-0 py-3">
                  <div class="d-flex align-items-center">
                    <div class="action-icon bg-info bg-opacity-10 rounded-circle p-2 me-3">
                      <i class="bi bi-funnel text-info"></i>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="mb-1">Filter Secondary Schools</h6>
                      <small class="text-muted">Search and filter applications</small>
                    </div>
                    <i class="bi bi-chevron-right text-muted"></i>
                  </div>
                </router-link>
                
                <router-link to="/filter/college" class="list-group-item list-group-item-action border-0 py-3">
                  <div class="d-flex align-items-center">
                    <div class="action-icon bg-info bg-opacity-10 rounded-circle p-2 me-3">
                      <i class="bi bi-funnel text-info"></i>
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="mb-1">Search Institutions</h6>
                      <small class="text-muted">Filter college/university applications</small>
                    </div>
                    <i class="bi bi-chevron-right text-muted"></i>
                  </div>
                </router-link>
              </div>
            </div>
          </div>

          <!-- Processing Status -->
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-0 py-3">
              <h5 class="mb-0 fw-bold">
                <i class="bi bi-clipboard-check-fill me-2 text-success"></i>
                Processing Status
              </h5>
            </div>
            <div class="card-body">
              <div class="mb-4">
                <h6 class="fw-bold mb-3">Secondary Schools</h6>
                <div class="d-flex align-items-center mb-2">
                  <small class="text-muted flex-grow-1">Approved</small>
                  <span class="badge bg-success rounded-pill">{{ secondary_processedApproved.length }}</span>
                </div>
                <div class="d-flex align-items-center mb-2">
                  <small class="text-muted flex-grow-1">Rejected</small>
                  <span class="badge bg-danger rounded-pill">{{ secondary_processedRejected.length }}</span>
                </div>
                <div class="d-flex align-items-center">
                  <small class="text-muted flex-grow-1">Pending</small>
                  <span class="badge bg-warning rounded-pill">{{ pendingSecondary }}</span>
                </div>
              </div>
              
              <div>
                <h6 class="fw-bold mb-3">College/University</h6>
                <div class="d-flex align-items-center mb-2">
                  <small class="text-muted flex-grow-1">Approved</small>
                  <span class="badge bg-success rounded-pill">{{ college_processedApproved.length }}</span>
                </div>
                <div class="d-flex align-items-center mb-2">
                  <small class="text-muted flex-grow-1">Rejected</small>
                  <span class="badge bg-danger rounded-pill">{{ college_processedRejected.length }}</span>
                </div>
                <div class="d-flex align-items-center">
                  <small class="text-muted flex-grow-1">Pending</small>
                  <span class="badge bg-warning rounded-pill">{{ pendingCollege }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Institutions Overview -->
      <div class="row g-4 mt-2">
        <div class="col-md-6">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-header bg-white border-0 py-3">
              <h5 class="mb-0 fw-bold">
                <i class="bi bi-mortarboard-fill me-2 text-primary"></i>
                Secondary Schools
              </h5>
            </div>
            <div class="card-body">
              <div class="text-center mb-4">
                <div class="display-6 fw-bold text-primary">{{ secondaryStudents.length }}</div>
                <small class="text-muted">Total Applications</small>
              </div>
              <div class="d-grid gap-2">
                <router-link to="/school/names" class="btn btn-outline-primary">
                  <i class="bi bi-eye me-2"></i> View All Secondary Schools
                </router-link>
                <router-link to="/filter/secondary" class="btn btn-primary">
                  <i class="bi bi-search me-2"></i> Filter & Search
                </router-link>
              </div>
            </div>
          </div>
        </div>
        
        <div class="col-md-6">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-header bg-white border-0 py-3">
              <h5 class="mb-0 fw-bold">
                <i class="bi bi-building-fill me-2 text-success"></i>
                Colleges & Universities
              </h5>
            </div>
            <div class="card-body">
              <div class="text-center mb-4">
                <div class="display-6 fw-bold text-success">{{ collegeStudents.length }}</div>
                <small class="text-muted">Total Applications</small>
              </div>
              <div class="d-grid gap-2">
                <router-link to="/college/names" class="btn btn-outline-success">
                  <i class="bi bi-eye me-2"></i> View All Institutions
                </router-link>
                <router-link to="/filter/college" class="btn btn-success">
                  <i class="bi bi-search me-2"></i> Search Institutions
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AdminDashboard',
  data() {
    return {
      currentDate: new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    };
  },
  computed: {

    //2026 works
    approvedCollege () {
      return this.approvedCollegeStudents.filter(approval => approval.user_remarks && approval.user_remarks.toLowerCase().includes("student approved")
  );
      
    },
       approvedSecondary () {
      return this.approvedSecondaryStudents.filter(approval => approval.user_remarks && approval.user_remarks.toLowerCase().includes("student approved")
  );
      
    },

    approvedCollegeStudents() {
      return this.$store.getters.approvedCollegeStudents;
    },

       approvedSecondaryStudents() {
      return this.$store.getters.approvedSecondaryStudents;
    },
    // Totals
    total_applicants() {
      return this.secondaryStudents.length + this.collegeStudents.length;
    },
    
    // Approved totals
    totalApproved() {
      return this.secondary_processedApproved.length + this.college_processedApproved.length;
    },
    
    // Rejected totals
    totalRejected() {
      return this.secondary_processedRejected.length + this.college_processedRejected.length;
    },
    
    // Pending totals
    pendingSecondary() {
      return this.secondaryStudents.length - 
             this.secondary_processedApproved.length - 
             this.secondary_processedRejected.length;
    },
    
    pendingCollege() {
      return this.collegeStudents.length - 
             this.college_processedApproved.length - 
             this.college_processedRejected.length;
    },
    
    totalPending() {
      return this.pendingSecondary + this.pendingCollege;
    },
    
    // Ward Statistics
    wardStats() {
      const wards = [
        { 
          name: 'Itibo Ward', 
          secondary: this.secondaryItibostudent.length, 
          college: this.collegeItibostudent.length,
          total: this.Itibo_student
        },
        { 
          name: 'Ekerenyo Ward', 
          secondary: this.secondaryEkerenyostudent.length, 
          college: this.collegeEkerenyostudent.length,
          total: this.Ekerenyo_student
        },
        { 
          name: 'Magwagwa Ward', 
          secondary: this.secondaryMagwagwastudent.length, 
          college: this.collegeMagwagwastudent.length,
          total: this.Magwagwa_student
        },
        { 
          name: 'Bokeira Ward', 
          secondary: this.secondaryBokeirastudent.length, 
          college: this.collegeBokeirastudent.length,
          total: this.Bokeira_student
        },
        { 
          name: 'Bomwagamo Ward', 
          secondary: this.secondaryBomwagamostudent.length, 
          college: this.collegeBomwagamostudent.length,
          total: this.Bomwagamo_student
        }
      ];
      
      // Calculate percentages
      return wards.map(ward => ({
        ...ward,
        percentage: this.total_applicants > 0 ? 
          Math.round((ward.total / this.total_applicants) * 100) : 0
      }));
    },
    
    // Needy case totals
    orphanTotal() {
      return this.collegeOrphan.length + this.secondaryOrphan.length;
    },
    
    singleParentTotal() {
      return this.collegeOneparent.length + this.SecondaryOneparent.length;
    },
    
    bothParentsTotal() {
      return this.collegeBothparents.length + this.SecondaryBothparent.length;
    },
    
    // Needy case percentages
    orphanPercentage() {
      return this.total_applicants > 0 ? 
        Math.round((this.orphanTotal / this.total_applicants) * 100) : 0;
    },
    
    singleParentPercentage() {
      return this.total_applicants > 0 ? 
        Math.round((this.singleParentTotal / this.total_applicants) * 100) : 0;
    },
    
    bothParentsPercentage() {
      return this.total_applicants > 0 ? 
        Math.round((this.bothParentsTotal / this.total_applicants) * 100) : 0;
    },
    
    // Processed data from second dashboard
    college_processed() {
      return this.collegeStudents.filter(process => process.processed == '1');
    },
    
    secondary_processed() {
      return this.secondaryStudents.filter(process => process.processed == '1');
    },
    
    // college_processedApproved() {
    //   return this.collegeStudents.filter(approval => approval.tracking_code == 'Approved');
    // },
   college_processedApproved() {
  return this.collegeStudents.filter(approval =>
    approval.user_remarks &&
    approval.user_remarks.toLowerCase().includes("student approved")
  );
},

    
    college_processedRejected() {
      return this.collegeStudents.filter(approval => approval.tracking_code == 'Rejected');
    },
    
    secondary_processedApproved() {
      return this.secondaryStudents.filter(approval => approval.tracking_code == 'Approved');
    },
    
    secondary_processedRejected() {
      return this.secondaryStudents.filter(approval => approval.tracking_code == 'Rejected');
    },
    
    // Individual ward counts
    Itibo_student() {
      return this.collegeItibostudent.length + this.secondaryItibostudent.length;
    },
    
    Ekerenyo_student() {
      return this.collegeEkerenyostudent.length + this.secondaryEkerenyostudent.length;
    },
    
    Magwagwa_student() {
      return this.collegeMagwagwastudent.length + this.secondaryMagwagwastudent.length;
    },
    
    Bokeira_student() {
      return this.collegeBokeirastudent.length + this.secondaryBokeirastudent.length;
    },
    
    Bomwagamo_student() {
      return this.collegeBomwagamostudent.length + this.secondaryBomwagamostudent.length;
    },
    
    // Getter functions from store
    secondaryStudents() {
      return this.$store.getters.secondaryStudents;
    },
    
    collegeStudents() {
      return this.$store.getters.collegeStudents;
    },
    
    // Ward-specific getters
    collegeItibostudent() {
      return this.$store.getters.collegeItibostudent;
    },
    
    secondaryItibostudent() {
      return this.$store.getters.secondaryItibostudent;
    },

    
    collegeEkerenyostudent() {
      return this.$store.getters.collegeEkerenyostudent;
    },
    
    secondaryEkerenyostudent() {
      return this.$store.getters.secondaryEkerenyostudent;
    },
    
    collegeMagwagwastudent() {
      return this.$store.getters.collegeMagwagwastudent;
    },
    
    secondaryMagwagwastudent() {
      return this.$store.getters.secondaryMagwagwastudent;
    },
    
    collegeBokeirastudent() {
      return this.$store.getters.collegeBokeirastudent;
    },
    
    secondaryBokeirastudent() {
      return this.$store.getters.secondaryBokeirastudent;
    },
    
    collegeBomwagamostudent() {
      return this.$store.getters.collegeBomwagamostudent;
    },
    
    secondaryBomwagamostudent() {
      return this.$store.getters.secondaryBomwagamostudent;
    },
    
    // Needy case getters
    collegeOrphan() {
      return this.$store.getters.collegeOrphan;
    },
    
    secondaryOrphan() {
      return this.$store.getters.secondaryOrphan;
    },
    
    SecondaryOneparent() {
      return this.$store.getters.SecondaryOneparent;
    },
    
    collegeOneparent() {
      return this.$store.getters.collegeOneparent;
    },
    
    collegeBothparents() {
      return this.$store.getters.collegeBothparents;
    },
    
    SecondaryBothparent() {
      return this.$store.getters.SecondaryBothparent;
    }
  },
  
  created() {
    this.$store.dispatch('LoadCollegestudents');
    this.$store.dispatch('LoadSecondarystudents');
    this.$store.dispatch('LoadApprovedCollegestudents');
    this.$store.dispatch('LoadApprovedSecondarystudents');


        


    
  },
  
  mounted() {
    // Load Bootstrap Icons if not already loaded
    if (!document.querySelector('link[href*="bootstrap-icons"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.8.1/font/bootstrap-icons.css';
      document.head.appendChild(link);
    }
  }
};
</script>

<style scoped>
.admin-dashboard {
  background-color: #f8f9fa;
  min-height: 100vh;
}

.dashboard-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.stat-card {
  transition: transform 0.2s ease-in-out;
  border-radius: 12px;
}

.stat-card:hover {
  transform: translateY(-5px);
}

.stat-icon {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ward-card, .needy-card {
  transition: all 0.3s ease;
  height: 100%;
}

.ward-card:hover, .needy-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
}

.progress {
  background-color: #e9ecef;
}

.action-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.list-group-item {
  border-radius: 8px !important;
  margin-bottom: 8px;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

/* Custom scrollbar */
.card-body {
  scrollbar-width: thin;
  scrollbar-color: #dee2e6 transparent;
}

.card-body::-webkit-scrollbar {
  width: 6px;
}

.card-body::-webkit-scrollbar-track {
  background: transparent;
}

.card-body::-webkit-scrollbar-thumb {
  background-color: #dee2e6;
  border-radius: 20px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .display-6 {
    font-size: 2rem;
  }
  
  .dashboard-header h1 {
    font-size: 1.5rem;
  }
}

/* Animation for cards */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stat-card, .ward-card, .needy-card {
  animation: fadeIn 0.5s ease-out;
}

/* Color utilities */
.bg-opacity-10 {
  opacity: 0.1;
}

.opacity-75 {
  opacity: 0.75;
}

/* Custom badge styles */
.badge.rounded-pill {
  padding: 0.5em 1em;
  font-weight: 500;
}
</style>